import { zoneListQuerySchema, type Page, type ZoneListQuery } from '@chukua/common';
import { useEffect, useState } from 'react';
import { ApiError } from '../../../../../shared/shared.api';
import { toListParams } from '../../../../../shared/policies/list-params';
import { useListQuery } from '../../../../../shared/hooks/use-list-query';
import { listZones, type ZoneListItem } from '../../../zones.api';

/** `queryKey` records which query the loaded page answers, so a newer query shows as refreshing over the old rows. */
export type ZoneListState =
  { status: 'loading' } | { status: 'loaded'; page: Page<ZoneListItem>; queryKey: string } | { status: 'forbidden' } | { status: 'error' };

const keyOf = (query: ZoneListQuery) => toListParams(query).toString();

function toState(page: Page<ZoneListItem>, query: ZoneListQuery): ZoneListState {
  return { status: 'loaded', page, queryKey: keyOf(query) };
}

function toErrorState(err: unknown): ZoneListState {
  return { status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' };
}

/** The page of zones the URL's list query asks for (`useListQuery`), re-fetched whenever that query changes. */
export function useZoneList() {
  const list = useListQuery(zoneListQuerySchema);
  const { query } = list;
  const [state, setState] = useState<ZoneListState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    listZones(query)
      .then((page) => {
        if (!cancelled) setState(toState(page, query));
      })
      .catch((err) => {
        if (!cancelled) setState(toErrorState(err));
      });

    return () => {
      cancelled = true;
    };
  }, [query]);

  const isRefreshing = state.status === 'loaded' && state.queryKey !== keyOf(query);

  /** Re-fetches the current page after a mutation commits — a plain async function, not tied to the effect above. */
  async function reload(): Promise<void> {
    try {
      setState(toState(await listZones(query), query));
    } catch (err) {
      setState(toErrorState(err));
    }
  }

  return { state, list, isRefreshing, reload };
}
